import type { Components } from 'react-markdown'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { useMemo, type ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import { Link, useLocation } from 'react-router-dom'
import remarkGfm from 'remark-gfm'
import { useAppSelector } from '../../shared/redux/hooks'
import {
  buildTenantAccountHref,
  extractAccountNumberFromHref,
  extractTenantIdFromAccountLinks,
  isKingAccountNumber,
} from './accountLinks'
import AccountNumberLink from './AccountNumberLink'
import { ChatAccountNamesProvider, getRememberedChatAccountNames, rememberChatAccountNames } from './chatAccountNames'
import {
  ChatEntityNamesProvider,
  getRememberedChatEntities,
  rememberChatEntities,
} from './chatEntityNames'
import { ChatLinkIcon } from './ChatLinkIcon'
import { resolveChatLink, transformChatMarkdownUrl } from './chatLinks'
import CuidEntityLink from './CuidEntityLink'
import {
  buildOnboardingCuidHref,
  buildProgramCuidHref,
  buildTariffCuidHref,
  buildTenantCuidHref,
  extractCuidFromAdminHref,
  isLikelyCuid,
  type KnownCuidMap,
} from './cuidExpand'
import JiraIssueLink from './JiraIssueLink'
import { extractJiraKeyFromHref, prepareChatMarkdown } from './jiraLinks'
import { useOnboardingViewFocus } from './onboardingViewFocus'
import { buildPageContext } from './pageContext'

const linkClass =
  'inline-flex max-w-full items-start font-medium text-indigo-600 no-underline hover:text-indigo-500 dark:text-indigo-300'
const linkLabelClass =
  'min-w-0 break-words underline underline-offset-2 [overflow-wrap:anywhere]'

const inlineCodeClass =
  'rounded bg-slate-200/80 px-1 py-0.5 font-mono text-[11px] text-slate-800 dark:bg-slate-700 dark:text-slate-100'

const ExternalLinkIcon = () => (
  <span className="ml-1 inline-flex h-[1lh] shrink-0 items-center" aria-hidden>
    <ArrowTopRightOnSquareIcon
      className="inline-block h-3.5 w-3.5 bg-transparent no-underline"
    />
  </span>
)

function withLinkIcon(
  children: ReactNode,
  opts?: { href?: string | null; kind?: 'internal' | 'jira' | 'external'; trailing?: ReactNode }
): ReactNode {
  return (
    <>
      <ChatLinkIcon kind={opts?.kind} href={opts?.href} />
      <span className={linkLabelClass}>{children}</span>
      {opts?.trailing}
    </>
  )
}

function buildComponents(tenantId: string | undefined, knownCuids: KnownCuidMap): Components {
  return {
    p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
    ul: ({ children }) => <ul className="mb-2 list-disc space-y-1 pl-4 last:mb-0">{children}</ul>,
    ol: ({ children }) => <ol className="mb-2 list-decimal space-y-1 pl-4 last:mb-0">{children}</ol>,
    li: ({ children }) => <li className="leading-snug">{children}</li>,
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    a: ({ href, children }) => {
      const issueKey = extractJiraKeyFromHref(href)
      if (issueKey) {
        return <JiraIssueLink issueKey={issueKey} href={href} className={linkClass} />
      }
      const accountNumber = extractAccountNumberFromHref(href)
      if (accountNumber) {
        const target = resolveChatLink(href)
        if (target?.kind !== 'internal') {
          return <span>{children}</span>
        }
        return <AccountNumberLink accountNumber={accountNumber} to={target.to} className={linkClass} />
      }
      const cuid = extractCuidFromAdminHref(href)
      if (cuid && knownCuids[cuid]) {
        const target = resolveChatLink(href)
        const to = target?.kind === 'internal' ? target.to : knownCuids[cuid]!.href
        return <CuidEntityLink cuid={cuid} to={to} className={linkClass} />
      }
      const target = resolveChatLink(href)
      if (target?.kind === 'internal') {
        return (
          <Link to={target.to} className={linkClass}>
            {withLinkIcon(children, { kind: 'internal', href: target.to })}
          </Link>
        )
      }
      if (target?.kind === 'external') {
        return (
          <a href={target.href} target="_blank" rel="noreferrer noopener" className={linkClass}>
            {withLinkIcon(children, { href: target.href, trailing: <ExternalLinkIcon /> })}
          </a>
        )
      }
      return <span>{children}</span>
    },
    code: ({ className, children }) => {
      const text = String(children).replace(/\n$/, '')
      const isBlock = Boolean(className?.includes('language-') || text.includes('\n'))
      if (!isBlock && tenantId && isKingAccountNumber(text)) {
        return (
          <AccountNumberLink
            accountNumber={text.trim()}
            to={buildTenantAccountHref(tenantId, text.trim())}
            className={linkClass}
          />
        )
      }
      if (!isBlock) {
        const trimmed = text.trim()
        if (isLikelyCuid(trimmed) && knownCuids[trimmed]) {
          return <CuidEntityLink cuid={trimmed} to={knownCuids[trimmed]!.href} className={linkClass} />
        }
      }
      if (isBlock) {
        return (
          <code className="block overflow-x-auto rounded-md bg-slate-900/90 px-2.5 py-2 font-mono text-[11px] leading-relaxed text-slate-100 dark:bg-black/50">
            {children}
          </code>
        )
      }
      return <code className={inlineCodeClass}>{children}</code>
    },
    pre: ({ children }) => <pre className="mb-2 overflow-x-auto last:mb-0">{children}</pre>,
    blockquote: ({ children }) => (
      <blockquote className="mb-2 border-l-2 border-slate-300 pl-3 text-slate-600 last:mb-0 dark:border-slate-600 dark:text-slate-300">
        {children}
      </blockquote>
    ),
    h1: ({ children }) => <h3 className="mb-1.5 text-sm font-semibold">{children}</h3>,
    h2: ({ children }) => <h3 className="mb-1.5 text-sm font-semibold">{children}</h3>,
    h3: ({ children }) => <h3 className="mb-1 text-sm font-semibold">{children}</h3>,
    hr: () => <hr className="my-2 border-slate-200 dark:border-slate-600" />,
    table: ({ children }) => (
      <div className="mb-2 max-w-full overflow-x-auto last:mb-0">
        <table className="w-full min-w-[12rem] border-collapse text-left text-[12px]">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-slate-100 dark:bg-slate-700/60">{children}</thead>,
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => (
      <tr className="border-b border-slate-200 dark:border-slate-600">{children}</tr>
    ),
    th: ({ children }) => (
      <th className="whitespace-nowrap px-2 py-1.5 font-semibold text-slate-800 dark:text-slate-100">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-2 py-1.5 align-top text-slate-700 dark:text-slate-200">{children}</td>
    ),
    del: ({ children }) => <del className="text-slate-500 line-through dark:text-slate-400">{children}</del>,
  }
}

type ChatMarkdownProps = {
  children: string
  /**
   * Tenant cuid used to linkify bare account numbers.
   * Prefer the message’s stored pageContext so links survive navigation.
   */
  linkTenantId?: string
}

const ChatMarkdown: React.FC<ChatMarkdownProps> = ({ children, linkTenantId }) => {
  const location = useLocation()
  const tenant = useAppSelector((state) => state.tenants.tenant)
  const program = useAppSelector((state) => state.programs.program)
  const tariff = useAppSelector((state) => state.tariffs.tariff)
  const onboardingViewFocus = useOnboardingViewFocus()

  const pageContext = useMemo(
    () => buildPageContext(location.pathname, location.search),
    [location.pathname, location.search]
  )

  const tenantId = useMemo(() => {
    if (linkTenantId) return linkTenantId
    const fromRoute = pageContext.params.tenantId
    if (fromRoute) return fromRoute
    const fromBody = extractTenantIdFromAccountLinks(children)
    if (fromBody) return fromBody
    if (tenant?.id && location.pathname.includes(`/tenant/tenants/${tenant.id}`)) {
      return tenant.id
    }
    return undefined
  }, [linkTenantId, pageContext.params.tenantId, children, tenant?.id, location.pathname])

  const accountNames = useMemo(() => {
    const names: Record<string, string> = {
      ...getRememberedChatAccountNames(tenantId),
    }
    if (tenantId && tenant?.serviceAccounts && tenant.id === tenantId) {
      for (const account of tenant.serviceAccounts) {
        if (account.kingAccountNumber && account.kingAccountName) {
          names[account.kingAccountNumber] = account.kingAccountName
        }
      }
      rememberChatAccountNames(tenantId, names)
    }
    return names
  }, [tenant, tenantId])

  const knownCuids = useMemo(() => {
    const map: KnownCuidMap = { ...getRememberedChatEntities() }
    if (tenant?.id && tenant.name) {
      map[tenant.id] = {
        id: tenant.id,
        name: tenant.name,
        href: buildTenantCuidHref(tenant.id),
      }
    }
    const programName = program?.name ?? program?.salesforceName
    if (program?.id && programName) {
      map[program.id] = {
        id: program.id,
        name: programName,
        href: buildProgramCuidHref(program.id),
      }
    }
    if (tariff?.cuid) {
      const tariffName =
        tariff.name != null && tariff.name !== ''
          ? `${tariff.schedule} · ${tariff.name}`
          : tariff.schedule
      map[tariff.cuid] = {
        id: tariff.cuid,
        name: tariffName,
        href: buildTariffCuidHref(tariff.cuid),
      }
    }
    if (onboardingViewFocus?.registrationId && onboardingViewFocus.businessName) {
      map[onboardingViewFocus.registrationId] = {
        id: onboardingViewFocus.registrationId,
        name: onboardingViewFocus.businessName,
        href: buildOnboardingCuidHref(onboardingViewFocus.registrationId),
      }
    }
    rememberChatEntities(map)
    return map
  }, [tenant?.id, tenant?.name, program?.id, program?.name, program?.salesforceName, tariff?.cuid, tariff?.schedule, tariff?.name, onboardingViewFocus])

  const components = useMemo(
    () => buildComponents(tenantId, knownCuids),
    [tenantId, knownCuids]
  )
  const markdown = useMemo(
    () => prepareChatMarkdown(children, { tenantId, knownCuids }),
    [children, tenantId, knownCuids]
  )

  return (
    <ChatEntityNamesProvider value={knownCuids}>
      <ChatAccountNamesProvider value={accountNames}>
        <div className="min-w-0 max-w-full break-words [overflow-wrap:anywhere] [&_:first-child]:mt-0">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            urlTransform={transformChatMarkdownUrl}
            components={components}
          >
            {markdown}
          </ReactMarkdown>
        </div>
      </ChatAccountNamesProvider>
    </ChatEntityNamesProvider>
  )
}

export default ChatMarkdown
